import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Bell, CheckCircle, Clock, MapPin, Battery, Wifi, Trash2, X } from 'lucide-react';
import { Alert } from '../lib/supabase';
import { formatDistanceToNow } from 'date-fns';

interface AlertsPanelProps {
  alerts: Alert[];
  loading: boolean;
  onDelete?: (alertId: string) => void;
  onClearAll?: () => void;
}

const AlertsPanel: React.FC<AlertsPanelProps> = ({ alerts, loading, onDelete, onClearAll }) => {
  const [showAll, setShowAll] = useState(false);
  
  const getSeverityColor = (severity: Alert['severity'], message?: string, type?: string) => {
    // Special colors for geofence alerts
    if (type === 'geofence_exit' || message?.includes('exited') || message?.includes('outside geofence')) {
      return 'border-orange-500 bg-orange-50 text-orange-700';
    }
    if (type === 'geofence_entry' || message?.includes('entered') || message?.includes('inside geofence')) {
      return 'border-green-500 bg-green-50 text-green-700';
    }
    
    // Default severity colors
    switch (severity) {
      case 'critical':
        return 'border-red-500 bg-red-50 text-red-700';
      case 'high':
        return 'border-orange-500 bg-orange-50 text-orange-700';
      case 'medium':
        return 'border-yellow-500 bg-yellow-50 text-yellow-700';
      case 'low':
        return 'border-blue-500 bg-blue-50 text-blue-700';
      default:
        return 'border-gray-500 bg-gray-50 text-gray-700';
    }
  };

  const getAlertIcon = (type: Alert['type'] | string) => {
    switch (type) {
      case 'geofence':
      case 'geofence_entry':
      case 'geofence_exit':
        return MapPin;
      case 'low_battery':
        return Battery;
      case 'offline':
        return Wifi;
      case 'speed':
        return AlertTriangle;
      case 'panic':
        return AlertTriangle;
      default:
        return Bell;
    }
  };

  const visibleAlerts = showAll ? alerts : alerts.slice(0, 3);

  if (loading) {
    return (
      <div className="apple-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 bg-traqpack-gray-200 rounded animate-pulse"></div>
            <div className="w-20 h-6 bg-traqpack-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="w-16 h-8 bg-traqpack-gray-200 rounded animate-pulse"></div>
        </div>
        
        <div className="space-y-3">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="p-4 border border-traqpack-gray-200 rounded-apple animate-pulse">
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 bg-traqpack-gray-200 rounded"></div>
                <div className="flex-1 space-y-2">
                  <div className="w-3/4 h-4 bg-traqpack-gray-200 rounded"></div>
                  <div className="w-1/2 h-3 bg-traqpack-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="apple-card p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <Bell className="w-6 h-6 text-traqpack-gray-700" />
            {alerts.length > 0 && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center"
              >
                {alerts.length}
              </motion.div>
            )}
          </div>
          <h2 className="text-xl font-semibold text-traqpack-gray-900">
            Alerts
          </h2>
          {alerts.length > 0 && (
            <span className="text-sm text-gray-600 font-medium">
              {alerts.length} active
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {alerts.length > 0 && onClearAll && (
            <button
              onClick={onClearAll}
              className="apple-button apple-button-secondary text-sm flex items-center space-x-1"
              title="Clear all alerts"
            >
              <Trash2 className="w-4 h-4" />
              <span>Clear All</span>
            </button>
          )}
          {alerts.length > 3 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="apple-button apple-button-secondary text-sm"
            >
              {showAll ? 'Show Less' : `View All (${alerts.length})`}
            </button>
          )}
        </div>
      </div>

      {alerts.length === 0 ? (
        <div className="text-center py-8">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-traqpack-gray-700 mb-2">All Clear!</h3>
          <p className="text-traqpack-gray-600">No alerts at this time. Your devices are operating normally.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {visibleAlerts.map((alert, index) => {
              const Icon = getAlertIcon(alert.type);
              const severityClasses = getSeverityColor(alert.severity, alert.message, alert.type);
              
              return (
                <motion.div
                  key={alert.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className={`p-4 border-l-4 rounded-apple transition-all duration-200 hover:shadow-md ${severityClasses}`}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <Icon className="w-5 h-5" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-semibold capitalize">
                          {(alert.type || 'general').replace('_', ' ')} Alert
                        </h4>
                      </div>
                      
                      <p className="text-sm mb-2">
                        {alert.message}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-medium bg-white bg-opacity-50 px-2 py-1 rounded">
                            Device {alert.device_id}
                          </span>
                          <span className="text-gray-600 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {(() => {
                              try {
                                if (!alert.timestamp) return 'Just now';
                                const date = new Date(alert.timestamp);
                                if (isNaN(date.getTime())) return 'Recently';
                                
                                // Format as both relative time and absolute time
                                const relative = formatDistanceToNow(date);
                                const absolute = date.toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true
                                });
                                return `${relative} ago (${absolute})`;
                              } catch (e) {
                                return 'Recently';
                              }
                            })()}
                          </span>
                        </div>
                        
                        {onDelete && (
                          <button
                            onClick={() => onDelete(alert.id)}
                            className="flex items-center space-x-1 text-xs px-3 py-1 bg-white bg-opacity-70 hover:bg-opacity-100 rounded transition-all duration-200 hover:text-red-600"
                            title="Delete this alert"
                          >
                            <X className="w-3 h-3" />
                            <span>Clear</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
      
      {alerts.length > 0 && (
        <div className="mt-6 pt-4 border-t border-traqpack-gray-200">
          <div className="flex items-center justify-between text-sm text-traqpack-gray-600">
            <span>
              {alerts.filter(a => a.severity === 'critical').length} critical, {' '}
              {alerts.filter(a => a.severity === 'high').length} high priority
            </span>
            <button className="text-traqpack-yellow hover:text-traqpack-yellow-dark transition-colors">
              Manage Alerts
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertsPanel;